import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const projectImages = [
  { image: 'https://images.unsplash.com/photo-1760072513367-55182245e76c?w=1200&q=80', span: 'col-span-1 lg:col-span-2 row-span-2', aspect: 'aspect-[4/5]', year: '2024' },
  { image: 'https://images.unsplash.com/photo-1682888813789-c39fe30921e2?w=800&q=80', span: 'col-span-1', aspect: 'aspect-square', year: '2024' },
  { image: 'https://images.unsplash.com/photo-1765766600820-58eaf8687f1d?w=800&q=80', span: 'col-span-1', aspect: 'aspect-square', year: '2023' },
  { image: 'https://images.unsplash.com/photo-1653064635129-e6e2eabfa9c7?w=800&q=80', span: 'col-span-1 lg:col-span-2', aspect: 'aspect-[16/9]', year: '2023' },
];

const FeaturedProjects = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const { t } = useLanguage();

  const projects = [
    { ...projectImages[0], title: t('projects.project1.title'), category: t('projects.project1.category') },
    { ...projectImages[1], title: t('projects.project2.title'), category: t('projects.project2.category') },
    { ...projectImages[2], title: t('projects.project3.title'), category: t('projects.project3.category') },
    { ...projectImages[3], title: t('projects.project4.title'), category: t('projects.project4.category') },
  ];

  return (
    <section id="projects" ref={ref} className="py-32 bg-[#141414] relative overflow-hidden">
      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      
      {/* Decorative corners */}
      <div className="absolute top-12 left-12 w-20 h-20 border-l-2 border-t-2 border-[#C5A55C]/30" />
      <div className="absolute top-12 right-12 w-20 h-20 border-r-2 border-t-2 border-[#C5A55C]/30" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="flex flex-col md:flex-row md:items-end md:justify-between mb-16"
        >
          <div>
            <motion.span 
              className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6"
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              {t('projects.tagline')}
            </motion.span>
            <h2 className="font-montserrat font-light text-4xl md:text-6xl text-white tracking-[0.1em] uppercase">
              {t('projects.title')}
            </h2>
            <div className="mt-6 w-24 h-[2px] bg-[#C5A55C]" />
          </div>
          
          <motion.button
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mt-8 md:mt-0 font-montserrat font-light text-xs tracking-[0.2em] text-white/50 hover:text-[#C5A55C] transition-colors uppercase flex items-center gap-2"
            data-testid="view-all-projects-btn"
          >
            {t('projects.viewAll')}
            <ArrowUpRight className="w-4 h-4" />
          </motion.button>
        </motion.div>

        {/* Projects Grid - Bento Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project, index) => (
            <ProjectCard
              key={index}
              project={project}
              inView={inView}
              delay={index * 0.15}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const ProjectCard = ({ project, inView, delay }) => {
  const cardRef = useRef(null);
  
  // Parallax effect using scroll position
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"]
  });
  
  // Transform the image position based on scroll (subtle parallax)
  const imageY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.8, ease: 'easeOut' }}
      className={`group relative overflow-hidden cursor-pointer ${project.span}`}
      data-testid={`project-card-${project.title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`relative overflow-hidden ${project.aspect}`}>
        {/* Parallax Image Container */}
        <motion.div
          style={{ y: imageY }}
          className="absolute inset-[-20%] w-[140%] h-[140%]"
        >
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover transition-all duration-700 ease-out scale-105 group-hover:scale-100"
          />
        </motion.div>
        
        {/* Overlay with gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent group-hover:from-black/50 group-hover:via-black/20 transition-all duration-500" />
        
        {/* Shimmer effect on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-[#C5A55C]/25 to-transparent" />
        </div>
        
        {/* Content */}
        <div className="absolute inset-0 p-8 flex flex-col justify-between z-10">
          {/* Top: Year */}
          <div className="flex justify-between items-start">
            <span className="font-montserrat font-light text-xs tracking-[0.2em] text-white/50">
              {project.year}
            </span>
            <div className="w-10 h-10 border border-white/20 group-hover:border-[#C5A55C] flex items-center justify-center transition-all duration-300 group-hover:bg-[#C5A55C] opacity-0 group-hover:opacity-100">
              <ArrowUpRight className="w-4 h-4 text-white group-hover:text-black transition-colors duration-300" />
            </div>
          </div>
          
          {/* Bottom: Title & Category */}
          <div>
            <span className="font-montserrat font-light text-[10px] tracking-[0.25em] text-[#C5A55C] uppercase block mb-2">
              {project.category}
            </span>
            <h3 className="font-montserrat font-light text-xl md:text-2xl text-white tracking-wide">
              {project.title}
            </h3>
          </div>
        </div>
        
        {/* Gold border on hover */}
        <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#C5A55C]/40 transition-all duration-500 pointer-events-none z-10" />
      </div>
    </motion.div>
  );
};

export default FeaturedProjects;
